import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Image from '@tiptap/extension-image'
import Link from '@tiptap/extension-link'
import Placeholder from '@tiptap/extension-placeholder'
import { Button } from './button'
import { cn } from '@/lib/utils'

const MenuBar = ({ editor }) => {
  if (!editor) {
    return null
  }

  return (
    <div className="border-b p-2 flex flex-wrap gap-2 bg-muted/30 rounded-t-md">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBold().run()}
        className={cn(
          'transition-colors',
          editor.isActive('bold')
            ? 'bg-primary text-primary-foreground shadow font-bold border border-primary'
            : 'hover:bg-primary/10',
        )}
      >
        <span className="font-bold">B</span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleItalic().run()}
        className={cn(
          'transition-colors',
          editor.isActive('italic')
            ? 'bg-primary text-primary-foreground shadow font-bold border border-primary'
            : 'hover:bg-primary/10',
        )}
      >
        <span className="italic">I</span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleStrike().run()}
        className={cn(
          'transition-colors',
          editor.isActive('strike')
            ? 'bg-primary text-primary-foreground shadow font-bold border border-primary'
            : 'hover:bg-primary/10',
        )}
      >
        <span className="line-through">S</span>
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleCode().run()}
        className={cn(
          'transition-colors',
          editor.isActive('code')
            ? 'bg-primary text-primary-foreground shadow font-bold border border-primary'
            : 'hover:bg-primary/10',
        )}
      >
        {'</>'}
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        className={cn(
          'transition-colors',
          editor.isActive('heading', { level: 1 })
            ? 'bg-primary text-primary-foreground shadow font-bold border border-primary'
            : 'hover:bg-primary/10',
        )}
      >
        H1
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={cn(
          'transition-colors',
          editor.isActive('heading', { level: 2 })
            ? 'bg-primary text-primary-foreground shadow font-bold border border-primary'
            : 'hover:bg-primary/10',
        )}
      >
        H2
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={cn(
          'transition-colors',
          editor.isActive('bulletList')
            ? 'bg-primary text-primary-foreground shadow font-bold border border-primary'
            : 'hover:bg-primary/10',
        )}
      >
        • List
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={cn(
          'transition-colors',
          editor.isActive('orderedList')
            ? 'bg-primary text-primary-foreground shadow font-bold border border-primary'
            : 'hover:bg-primary/10',
        )}
      >
        1. List
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={cn(
          'transition-colors',
          editor.isActive('codeBlock')
            ? 'bg-primary text-primary-foreground shadow font-bold border border-primary'
            : 'hover:bg-primary/10',
        )}
      >
        [Code]
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        className={cn(
          'transition-colors',
          editor.isActive('blockquote')
            ? 'bg-primary text-primary-foreground shadow font-bold border border-primary'
            : 'hover:bg-primary/10',
        )}
      >
        ❝
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()}
        className="transition-colors hover:bg-primary/10"
      >
        Undo
      </Button>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()}
        className="transition-colors hover:bg-primary/10"
      >
        Redo
      </Button>
    </div>
  )
}

export function RichTextEditor({ content, onChange, placeholder = 'Write something...' }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      Link.configure({
        openOnClick: false,
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML())
    },
  })

  return (
    <div className="border rounded-md">
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className="prose prose-sm max-w-none p-4 min-h-[150px]" />
    </div>
  )
} 